<template>
  <section id="printEmployees" style="width: 100%;">
    <v-row align="center" no-gutters class="mb-3">
      <v-col cols="auto" class="pl-0">
        <div class="title text--secondary mt-2 px-4 text-uppercase">
          {{ $t("employees.title") }}
        </div>
      </v-col>
      <v-col class="pa-0" cols="auto">
        <v-row class="ma-0">
          <v-checkbox
            v-if="
              role(
                'manager',
                'departmentManager',
                'admin',
                'safetyRepresentative'
              )
            "
            v-model="showAll.departments"
            hide-details
            dense
            :label="$t('employees.showAll')"
            class="ma-0 mr-6 pt-2"
            :loading="$apollo.loading || loading"
          />
          <v-checkbox
            v-if="
              role('manager', 'departmentManager') &&
                employees &&
                employees.length &&
                employees.every((e) => !e.manager || e.manager.id != me.id)
            "
            v-model="showAll.shifts"
            hide-details
            dense
            :label="$t('employees.showAllShifts')"
            class="ma-0 pt-2"
            :loading="$apollo.loading || loading"
            v-show="!showAll.departments"
          />
        </v-row>
      </v-col>
      <v-col class="text-end hide-on-print">
        <v-btn class="primary" @click="print">
          <v-icon>mdi-printer</v-icon>
        </v-btn>
        <v-menu offset-y>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              v-if="role('admin', 'safetyRepresentative')"
              class="primary ml-3"
              :loading="$apollo.loading || loading"
            >
              {{ $t("import.import") }}
              <v-icon dark right>mdi-dots-vertical</v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-list-item @click="importEmployees()">
              <v-list-item-title>UKG</v-list-item-title>
            </v-list-item>
                        <v-list-item @click="$refs.ukgFile.click()">
              <v-list-item-title>
                {{ $t("import.uploadUkg") }}
              </v-list-item-title>
            </v-list-item>
            <v-list-item @click="importPeoplePoint()">
              <v-list-item-title>PeoplePoint</v-list-item-title>
            </v-list-item>
            <v-list-item @click="$refs.peoplePointFile.click()">
              <v-list-item-title>
                {{ $t("import.uploadPeoplePoint") }}
              </v-list-item-title>
            </v-list-item>
            <v-list-item @click="showPeoplePointComparison = true">
              <v-list-item-title>
                {{ $t("import.comparePeoplePoint") }}
              </v-list-item-title>
            </v-list-item>
            <input
              type="file"
              accept=".csv"
              ref="peoplePointFile"
              style="display: none"
              @change="uploadPeoplePoint"
            />
            <input
              type="file"
              accept=".csv"
              ref="ukgFile"
              style="display: none"
              @change="uploadUkg"
            />
          </v-list>
        </v-menu>
        <import-manual
          @importResult="showImportResult"
          @importError="showImportError"
        />
      </v-col>
    </v-row>

    <employees-table
      :employees="employees"
      :tagCategories="tagCategories"
      :loading="$apollo.loading || loading"
      @refresh="$apollo.queries.employees.refresh()"
    />

    <v-snackbar v-model="hasError" left color="red" multi-line :timeout="10000">
      {{ error }}
      <v-btn
        text
        @click="
          () => {
            error = null;
            $router.push('/reports');
          }
        "
        >{{ $t("import.notification.showLog") }}</v-btn
      >
    </v-snackbar>
    <import-notification
      :show="showImportNotification"
      :ir="importResult"
      @close-notification="showImportNotification = false"
    />
    <people-point-comparison
      v-if="showPeoplePointComparison"
      :show="showPeoplePointComparison"
      @close-comparison="showPeoplePointComparison = false"
      @imported="showImportResult($event)"
    />
  </section>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import EmployeesQuery from "@/graphql/Employees.gql";
import ImportQuery from "@/graphql/Import.gql";
import ImportPeoplePointQuery from "@/graphql/mutation/ImportPeoplePoint.gql";
import TagCategoriesQuery from "@/graphql/TagCategories.gql";
import UploadPeoplePointMutation from "@/graphql/mutation/UploadPeoplePoint.gql";
import UploadUkgMutation from "@/graphql/mutation/UploadUkg.gql";

import ImportNotification from "@/components/ImportNotification.vue";
import ImportManual from "@/components/ImportManual.vue";
import EmployeesTable from "@/components/employee/EmployeesTable.vue";
import PeoplePointComparison from "@/components/employee/PeoplePointComparison.vue";
import {
  createTagCategories,
  createEmployeesSearch,
  getMissingParentTags,
} from "@/utils/common.js";

export default {
  components: {
    ImportNotification,
    ImportManual,
    EmployeesTable,
    PeoplePointComparison,
  },
  data() {
    return {
      employees: [],
      showAll: {
        departments: false,
        shifts: false,
      },
      error: null,
      loading: false,
      importResult: {},
      showImportNotification: false,
      showPeoplePointComparison: false,
    };
  },

  watch: {
    showAll: {
      handler() {
        this.$store.dispatch("show_all", this.showAll);
      },
      deep: true,
    },
  },

  mounted() {
    this.showAll = {
      departments: this.showAllEmployees,
      shifts: this.showAllShifts,
    };
  },

  computed: {
    ...mapState(["me"]),
    ...mapGetters([
      "role",
      "employeeFilters",
      "showAllEmployees",
      "showAllShifts",
      "employeeHeaders",
    ]),

    hasError: {
      get() {
        return !!this.error;
      },
      set(newVal) {
        this.error = newVal;
      },
    },
  },

  methods: {
    importEmployees() {
      this.error = null;
      this.loading = true;
      this.showImportNotification = false;
      this.importResult = {};
      this.$apollo
        .mutate({ mutation: ImportQuery })
        .then((res) => {
          this.showImportResult(res.data.import);
        })
        .catch((err) => {
          this.showImportError(err);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    importPeoplePoint() {
      this.error = null;
      this.loading = true;
      this.$apollo
        .mutate({ mutation: ImportPeoplePointQuery })
        .then(() => {
          this.$store.commit("push_global_message", {
            text: `${this.$t("import.importSuccess")}`,
            color: "success",
          });
        })
        .catch((err) => {
          this.error = err;
        })
        .finally(() => {
          this.$apollo.queries.employees.refresh();
          this.loading = false;
        });
    },

    showImportResult(data) {
      this.importResult = data;
      this.showImportNotification = false;
      this.$nextTick(() => {
        this.showImportNotification = true;
      });
      this.$apollo.queries.employees.refresh();
    },

    uploadPeoplePoint() {
      const file = this.$refs.peoplePointFile.files[0];
      if (!file) {
        return;
      }

      this.error = null;
      this.loading = true;

      this.$apollo
        .mutate({
          mutation: UploadPeoplePointMutation,
          variables: {
            file,
          },
        })
        .then(() => {
          this.$store.commit("push_global_message", {
            text: `${this.$t("import.uploadSuccess")}`,
            color: "success",
          });
          this.$refs.peoplePointFile.value = null;
        })
        .catch((err) => {
          this.error = err.graphQLErrors[0].message;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    uploadUkg() {
      const file = this.$refs.ukgFile.files[0];
      if (!file) {
        return;
      }

      this.error = null;
      this.loading = true;

      this.$apollo
        .mutate({
          mutation: UploadUkgMutation,
          variables: {
            file,
          },
        })
        .then(() => {
          this.$store.commit("push_global_message", {
            text: `${this.$t("import.uploadSuccess")}`,
            color: "success",
          });
          this.$refs.ukgFile.value = null;
        })
        .catch((err) => {
          this.error = err.graphQLErrors[0].message;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    showImportError(err) {
      this.error = `${this.$t("employees.alert.importFailed")}:
          ${err.graphQLErrors[0].message}`;
    },
    print() {
      window.print();
    },
  },

  apollo: {
    tagCategories: {
      query: TagCategoriesQuery,
    },
    employees: {
      query: EmployeesQuery,
      variables() {
        return {
          queryAll: this.showAll.departments,
          queryAllShifts: this.showAll.shifts,
        };
      },
      update(data) {
        return (
          data.employees &&
          data.employees.map((e) => {
            const missingParentTags = getMissingParentTags(e);
            const search = `${createEmployeesSearch(e, this.employeeHeaders)}${
              missingParentTags.length ? "|*" : ""
            }`;
            return {
              ...e,
              search,
              ...createTagCategories(this.tagCategories, e.tags),
              missingParentTags,
            };
          })
        );
      },
      skip() {
        return !this.tagCategories;
      },
    },
  },
};
</script>

<style>
.minWidth150 {
  min-width: 150px;
}
@media print {
  body {
    visibility: hidden;
  }
  #printEmployees {
    visibility: visible;
    margin-top: -120px;
  }
  .hide-on-print {
    display: none !important;
  }
}
</style>
